import { z } from 'zod';

import { PAYMENTS_TERMS_VERSION } from '@/components/legal/payments-terms-section';
import {
  PAYMENT_ACCOUNT_ACCOUNT_TYPE,
  PAYMENT_ACCOUNT_BUSINESS_TYPE,
  PaymentAccountActivationDraft,
  PaymentAccountActivationPayload,
} from '@/types/payment-account';
import {
  brDateToIso,
  isAdultBrDate,
  isValidBankAccount,
  isValidBankAccountDigit,
  isValidBankAgency,
  isValidCep,
  isValidCnpj,
  isValidCpf,
  onlyDigits,
} from '@/utils/br-validators';

/**
 * Modelo do assistente de ativação (PLANO, seção 6.3 — etapas 1 a 5, T4.3).
 * O schema é a fonte da regra ("calm-ui", seção 5): `trigger()` só barra
 * porque todo campo tem validação aqui, inclusive as condicionais
 * (`companyDocument` obrigatório só com `businessType === COMPANY`).
 *
 * Espelha `CreatePaymentAccountDto`
 * (`api/src/payment-account/dto/create-payment-account.dto.ts`, fonte da
 * verdade) campo a campo, exceto pela forma bruta que o formulário guarda
 * (com máscara) — `toActivationPayload` converte para o que a API espera.
 */
export const activationSchema = z
  .object({
    businessType: z.nativeEnum(PAYMENT_ACCOUNT_BUSINESS_TYPE),
    legalName: z
      .string()
      .trim()
      .min(1, 'Informe seu nome como está no CPF.')
      .max(200, 'Nome muito longo.'),
    document: z
      .string()
      .refine(
        (v) => isValidCpf(v),
        'Esse CPF não parece válido. Confira os números e tente de novo.',
      ),
    companyDocument: z.string().optional(),
    birthDate: z
      .string()
      .min(1, 'Informe sua data de nascimento.')
      .refine(
        (v) => isAdultBrDate(v, 18),
        'É preciso ter 18 anos ou mais para ativar o recebimento automático.',
      ),
    address: z.object({
      zipCode: z.string().refine((v) => isValidCep(v), 'CEP inválido. Confira os números.'),
      street: z.string().trim().min(1, 'Informe a rua.').max(200),
      number: z.string().trim().min(1, 'Informe o número.').max(20),
      complement: z.string().max(200).optional(),
      neighborhood: z.string().trim().min(1, 'Informe o bairro.').max(100),
      city: z.string().trim().min(1, 'Informe a cidade.').max(100),
      state: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{2}$/, 'Use a sigla do estado (ex.: SP).'),
    }),
    isPoliticallyExposed: z.boolean({
      required_error: 'Responda se você é uma pessoa politicamente exposta.',
    }),
    bank: z.object({
      bankCode: z.string().min(1, 'Escolha o banco.'),
      bankName: z.string().min(1, 'Escolha o banco.'),
      agency: z.string().refine((v) => isValidBankAgency(v), 'Agência inválida.'),
      account: z.string().refine((v) => isValidBankAccount(v), 'Número da conta inválido.'),
      accountDigit: z
        .string()
        .optional()
        .refine((v) => isValidBankAccountDigit(v), 'Dígito da conta inválido.'),
      accountType: z.nativeEnum(PAYMENT_ACCOUNT_ACCOUNT_TYPE, {
        required_error: 'Escolha corrente ou poupança.',
      }),
    }),
    acceptedTerms: z.boolean().refine((v) => v === true, {
      message: 'Aceite os termos de recebimento para continuar.',
    }),
  })
  .superRefine((data, ctx) => {
    if (data.businessType === PAYMENT_ACCOUNT_BUSINESS_TYPE.COMPANY) {
      if (!data.companyDocument || !isValidCnpj(data.companyDocument)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['companyDocument'],
          message: 'Esse CNPJ não parece válido. Confira os números e tente de novo.',
        });
      }
    }
  });

export type ActivationForm = z.infer<typeof activationSchema>;

/** Campos de cada etapa (1 a 5) — o portão do assistente valida só estes (calm-ui, seção 5). */
export const ACTIVATION_STEP_FIELDS: (
  | keyof ActivationForm
  | `address.${string}`
  | `bank.${string}`
)[][] = [
  ['businessType', 'legalName', 'document', 'companyDocument', 'birthDate'],
  [
    'address.zipCode',
    'address.street',
    'address.number',
    'address.complement',
    'address.neighborhood',
    'address.city',
    'address.state',
  ],
  ['isPoliticallyExposed'],
  [
    'bank.bankCode',
    'bank.bankName',
    'bank.agency',
    'bank.account',
    'bank.accountDigit',
    'bank.accountType',
  ],
  ['acceptedTerms'],
];

export const ACTIVATION_DEFAULT_VALUES: ActivationForm = {
  businessType: PAYMENT_ACCOUNT_BUSINESS_TYPE.INDIVIDUAL,
  legalName: '',
  document: '',
  companyDocument: '',
  birthDate: '',
  address: {
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  },
  // Nada pré-marcado (calm-ui, seção 5): fica indefinido até o prestador escolher.
  isPoliticallyExposed: undefined as unknown as boolean,
  bank: {
    bankCode: '',
    bankName: '',
    agency: '',
    account: '',
    accountDigit: '',
    accountType: undefined as unknown as PAYMENT_ACCOUNT_ACCOUNT_TYPE,
  },
  acceptedTerms: false,
};

/**
 * Só com `EXPO_PUBLIC_TEST_TOOLS=true`: tudo preenchido com os dados de teste do roteiro da
 * T9.1 (`docs/flows/payment-account/roteiro-teste-t9.1.md`, tabela "Dados de
 * teste") — CPF com dígito válido e conta bancária que o Stripe aceita em
 * modo teste (repasse funciona). Rua `address_full_match` e nascimento em
 * 01/01/1901 são os valores mágicos do Stripe para o endereço e a identidade
 * verificarem (os mesmos do spike T0.2, `api/scripts/spike-connect.ts`); a API
 * manda o token sozinho no `line1`, sem o número.
 */
export const DEV_ACTIVATION_TEST_VALUES: ActivationForm = {
  businessType: PAYMENT_ACCOUNT_BUSINESS_TYPE.INDIVIDUAL,
  legalName: 'Prestador Teste',
  document: '529.982.247-25',
  companyDocument: '',
  birthDate: '01/01/1901',
  address: {
    zipCode: '01310-100',
    street: 'address_full_match',
    number: '1000',
    complement: '',
    neighborhood: 'Bela Vista',
    city: 'São Paulo',
    state: 'SP',
  },
  isPoliticallyExposed: false,
  bank: {
    bankCode: '001',
    bankName: 'Banco do Brasil',
    agency: '0000',
    account: '0001234',
    accountDigit: '',
    accountType: PAYMENT_ACCOUNT_ACCOUNT_TYPE.CHECKING,
  },
  acceptedTerms: true,
};

/** `ActivationForm` → corpo de `POST /payment-account` (nomes e formato exatos do DTO real). */
export function toActivationPayload(form: ActivationForm): PaymentAccountActivationPayload {
  const isCompany = form.businessType === PAYMENT_ACCOUNT_BUSINESS_TYPE.COMPANY;
  return {
    businessType: form.businessType,
    legalName: form.legalName.trim(),
    document: onlyDigits(form.document),
    companyDocument: isCompany ? onlyDigits(form.companyDocument) : undefined,
    birthDate: brDateToIso(form.birthDate),
    address: {
      zipCode: onlyDigits(form.address.zipCode),
      street: form.address.street.trim(),
      number: form.address.number.trim(),
      complement: form.address.complement?.trim() || undefined,
      neighborhood: form.address.neighborhood.trim(),
      city: form.address.city.trim(),
      state: form.address.state.trim().toUpperCase(),
    },
    isPoliticallyExposed: form.isPoliticallyExposed,
    bank: {
      bankCode: form.bank.bankCode,
      bankName: form.bank.bankName,
      agency: onlyDigits(form.bank.agency),
      account: onlyDigits(form.bank.account),
      accountDigit: form.bank.accountDigit?.trim() || undefined,
      accountType: form.bank.accountType,
    },
    acceptedTermsVersion: PAYMENTS_TERMS_VERSION,
  };
}

/**
 * Só endereço e banco vão para o rascunho local (PLANO, seção 2.5): CPF,
 * nascimento e PEP nunca tocam o `AsyncStorage`.
 */
export function toDraft(form: ActivationForm): PaymentAccountActivationDraft {
  return {
    businessType: form.businessType,
    address: { ...form.address },
    bank: { ...form.bank },
  };
}

export function applyDraft(
  defaults: ActivationForm,
  draft: PaymentAccountActivationDraft | null,
): ActivationForm {
  if (!draft) return defaults;
  return {
    ...defaults,
    businessType: draft.businessType ?? defaults.businessType,
    address: { ...defaults.address, ...draft.address },
    bank: { ...defaults.bank, ...draft.bank },
  };
}

/** Em qual etapa (0 a 4) mora um campo do formulário — usado pra pular direto no erro do servidor. */
export function stepForField(field: string): number {
  if (field.startsWith('address')) return 1;
  if (field === 'isPoliticallyExposed') return 2;
  if (field.startsWith('bank')) return 3;
  if (field === 'acceptedTerms' || field === 'acceptedTermsVersion') return 4;
  return 0;
}

/**
 * `field` devolvido pelo servidor (400 `{ field, message }` ou o Stripe
 * traduzido, `stripe-error-translator.ts`) → caminho do formulário. `bank`
 * sozinho (erro geral da conta) aponta pro número da conta, que é o culpado
 * mais comum. `email` não tem campo no formulário — vira só o toast.
 */
export function mapServerFieldToFormPath(field?: string): string | undefined {
  if (!field) return undefined;
  if (field === 'bank') return 'bank.account';
  if (field === 'email') return undefined;
  return field;
}
